import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  Alert,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { api } from '../contexts/ApiContext';

export type TxDocumentType = 'receipt' | 'sales_invoice' | 'purchase_invoice';

/** Minimal transaction shape needed to upload + link a document. */
export interface TxDocumentTarget {
  /** DB primary key of the transaction (RevolutTransactionComplete.id) — required for linking. */
  id: number;
  description?: string | null;
  /** ISO date (YYYY-MM-DD) used to prefill the invoice/receipt date. */
  dateIso?: string | null;
  supplier?: string | null;
}

interface TxDocumentUploadDialogProps {
  open: boolean;
  transaction: TxDocumentTarget | null;
  initialDocType?: TxDocumentType;
  onClose: () => void;
  onUploaded?: (info: { transactionId: number; documentType: TxDocumentType; uid: string }) => void;
}

const DOC_TYPE_LABELS: Record<TxDocumentType, string> = {
  receipt: 'Receipt',
  sales_invoice: 'Sales Invoice',
  purchase_invoice: 'Purchase Invoice',
};

/**
 * Self-contained dialog that uploads a document to the shared documents library
 * (POST /api/documents/upload) and links it to a specific transaction
 * (POST /api/transaction-documents/link). Mirrors the flow used on the VAT page
 * so it can be reused from the Transactions page (incl. the daily-email deep link).
 */
export const TxDocumentUploadDialog: React.FC<TxDocumentUploadDialogProps> = ({
  open,
  transaction,
  initialDocType = 'receipt',
  onClose,
  onUploaded,
}) => {
  const [docType, setDocType] = useState<TxDocumentType>(initialDocType);
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  // Prefill fields whenever the dialog opens for a (new) transaction.
  useEffect(() => {
    if (!open || !transaction) return;
    setDocType(initialDocType);
    setFile(null);
    setError('');
    setDescription(String(transaction.description || '').trim());
    setInvoiceNumber(transaction.id ? `TX-${transaction.id}` : '');
    setInvoiceDate(String(transaction.dateIso || '').trim());
    setSupplierName(String(transaction.supplier || '').trim());
  }, [open, transaction, initialDocType]);

  const handleClose = () => {
    if (uploading) return;
    onClose();
  };

  const handleUpload = async () => {
    if (!transaction?.id) {
      setError('No transaction selected to attach the document to.');
      return;
    }
    if (!file) {
      setError('Please choose a file to upload');
      return;
    }
    if (docType !== 'receipt' && (!invoiceNumber.trim() || !invoiceDate.trim())) {
      setError('Invoice number and invoice date are required');
      return;
    }
    if (docType === 'purchase_invoice' && !supplierName.trim()) {
      setError('Supplier name is required for purchase invoices');
      return;
    }
    try {
      setUploading(true);
      setError('');
      const formData = new FormData();
      formData.append('document_type', docType);
      formData.append('file', file);
      if (description.trim()) formData.append('description', description.trim());
      if (docType !== 'receipt') {
        formData.append('invoice_number', invoiceNumber.trim());
        formData.append('invoice_date', invoiceDate.trim());
      }
      if (docType === 'purchase_invoice') {
        formData.append('supplier_name', supplierName.trim());
      }
      if (docType === 'receipt' && invoiceDate.trim()) {
        formData.append('receipt_date', invoiceDate.trim());
      }
      // Let the browser set the multipart boundary; allow longer than the default 90s API timeout.
      const uploadRes = await api.post('/api/documents/upload', formData, { timeout: 180000 });
      const uid = String(uploadRes.data?.uid || '').trim();
      if (!uid) {
        throw new Error('Upload succeeded but document uid was missing');
      }
      await api.post('/api/transaction-documents/link', {
        transaction_id: transaction.id,
        document_type: docType,
        document_uid: uid,
      });
      onUploaded?.({ transactionId: transaction.id, documentType: docType, uid });
      setFile(null);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Upload {DOC_TYPE_LABELS[docType]}
        {transaction?.id ? ` for TX ${transaction.id}` : ''}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            The file is stored in the same documents library as Receipts &amp; Invoices, then linked to
            this transaction.
          </Typography>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <FormControl fullWidth size="small" disabled={uploading}>
            <InputLabel id="tx-doc-type-label">Document type</InputLabel>
            <Select
              labelId="tx-doc-type-label"
              label="Document type"
              value={docType}
              onChange={(e) => setDocType(e.target.value as TxDocumentType)}
            >
              <MenuItem value="receipt">Receipt</MenuItem>
              <MenuItem value="sales_invoice">Sales Invoice</MenuItem>
              <MenuItem value="purchase_invoice">Purchase Invoice</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" component="label" disabled={uploading}>
            {file ? file.name : 'Choose file'}
            <input
              type="file"
              hidden
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </Button>
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            size="small"
            disabled={uploading}
          />
          {docType !== 'receipt' ? (
            <>
              <TextField
                label="Invoice Number"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                fullWidth
                size="small"
                required
                disabled={uploading}
              />
              <TextField
                label="Invoice Date"
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                fullWidth
                size="small"
                required
                disabled={uploading}
                InputLabelProps={{ shrink: true }}
              />
            </>
          ) : (
            <TextField
              label="Receipt Date"
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              fullWidth
              size="small"
              disabled={uploading}
              InputLabelProps={{ shrink: true }}
            />
          )}
          {docType === 'purchase_invoice' ? (
            <TextField
              label="Supplier Name"
              value={supplierName}
              onChange={(e) => setSupplierName(e.target.value)}
              fullWidth
              size="small"
              required
              disabled={uploading}
            />
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleUpload()}
          disabled={uploading || !file}
        >
          {uploading ? 'Uploading…' : 'Upload & link'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TxDocumentUploadDialog;
